import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export interface ContactGroupsContact {
  id: string;
  name: string;
  role?: string;
  mobile?: string;
  email?: string;
  profile_image_url?: string;
}

export interface ContactGroupsGroup {
  id: string;
  name: string;
  contacts: ContactGroupsContact[];
}

interface RawGroup {
  contact_group_id?: string;
  name?: string;
  order_index?: number;
}

interface RawContact {
  contact_id?: string;
  title?: string | null;
  first_name?: string | null;
  surname?: string | null;
  email?: string | null;
  heading?: string | null;
  designation?: string | null;
  description?: string | null;
  mobile_number?: string | null;
  home_number?: string | null;
  additional_number?: string | null;
  picture_url?: string | null;
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  member?: { first_name?: string | null; last_name?: string | null; email?: string | null } | null;
}

function contactName(contact: RawContact): string {
  const contactParts = [contact.title, contact.first_name, contact.surname].filter(Boolean);
  if (contact.show_name !== false && contactParts.length > 0) {
    return contactParts.join(' ').trim();
  }

  const memberParts = [contact.member?.first_name, contact.member?.last_name].filter(Boolean);
  if (contact.show_name !== false && memberParts.length > 0) {
    return memberParts.join(' ').trim();
  }

  return contact.heading || contact.member?.email || 'Contact';
}

function toContact(contact: RawContact, group: RawGroup, fallbackId: string): ContactGroupsContact {
  return {
    id: contact.contact_id ?? fallbackId,
    name: contactName(contact),
    role: contact.designation ?? contact.description ?? contact.heading ?? group.name ?? undefined,
    email: contact.show_email === false ? undefined : contact.email ?? undefined,
    mobile: contact.show_phone === false
      ? undefined
      : contact.mobile_number ?? contact.home_number ?? contact.additional_number ?? undefined,
    profile_image_url: contact.picture_url ?? undefined,
  };
}

export async function fetchConfiguredContactGroups(groupIds: string[]): Promise<ContactGroupsGroup[]> {
  const uniqueGroupIds = [...new Set(groupIds.map((id) => id.trim()).filter(Boolean))];
  if (uniqueGroupIds.length === 0) {
    return [];
  }

  return (await Promise.all(
    uniqueGroupIds.map(async (groupId, groupIndex) => {
      const [groupRes, contactsRes] = await Promise.all([
        tenantApi.get(`/contact-groups/${groupId}`),
        tenantApi.get(`/contact-groups/${groupId}/contacts/member`),
      ]);
      const group = unwrap<RawGroup>(groupRes.data);
      const contacts = unwrapList<RawContact>(contactsRes.data);

      return {
        id: group.contact_group_id ?? groupId,
        name: group.name ?? 'Contact Group',
        order_index: Number(group.order_index ?? groupIndex),
        contacts: contacts.map((contact, contactIndex) => toContact(contact, group, `c-${groupIndex}-${contactIndex}`)),
      };
    }),
  ))
    .filter((group) => group.contacts.length > 0)
    .sort((left, right) => {
      const orderDiff = Number(left.order_index ?? 0) - Number(right.order_index ?? 0);
      return orderDiff !== 0 ? orderDiff : left.name.localeCompare(right.name);
    })
    .map(({ order_index: _orderIndex, ...group }) => group);
}
